import './SegmentRunConfigFilters.scss';
import React, {forwardRef, ReactNode, useContext, useEffect, useImperativeHandle, useRef, useState} from "react";
import classnames from "classnames";
import {DemographicCondition, ReportRunConfig} from "@/types/ReportRun";
import {FilterGroup, FilterSection} from "@/components/ConfigurationEditors/FilterGroup";
import {DemographicDimensionSelect} from "@/components/DemographicDimensionSelect";
import {
    ReportRunConfigWizardContext,
    ReportRunWizardState
} from "@/components/ConfigurationEditors/ReportConfigurationEditor/ReportRunConfigWizard/ReportRunConfigWizardContext";
import {DemographicCriteriaBuilder} from '@/components/DemographicCriteriaBuilder';
import {ByzzerRadio, ByzzerRadioGroup} from "@byzzer/ui-components";
import { SelectorLabelInfo } from '@/components/SelectorLabelInfo';
import { ByzzerOshDemographicCriteriaBuilder } from '@/components/ByzzerOshDemographicCriteriaBuilder';
import { ByzzerOshDemographicDimensionSelect } from '@/components/ByzOshDemographicDimensionSelect';

export type SegmentRunConfigFilterValue = Partial<ReportRunConfig>;

export type SegmentRunConfigFiltersProps = {
    name?: string;
    onChange?: (e: ByzzerChangeEvent<SegmentRunConfigFilterValue>) => void;
    onValidityChange?: (e: ByzzerValidityChangeEvent) => void;
    value?: SegmentRunConfigFilterValue;
    summary?: ReactNode;
    datatype?: string;
    includeDemographics?: boolean;
    includeDemographicDimensions?: boolean;
    includeOmniShopperDemographics?: boolean;
    requireDemographics?: boolean;
    requireDemographicDimensions?: boolean;
    requireOmniShopperDemographics?: boolean;
    includeOmniShopperDemographicDimension?: boolean;
    requireOmniShopperDemographicDimension?: boolean;
    maxDemographics?: number;
    maxDemographicDimensions?: number;
    maxDemographicConditions?: number;
    maxDemographicValue?: number;
} & Partial<Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>>;

export type SegmentRunConfigFiltersRef = {
    value: SegmentRunConfigFiltersValue;
    isValid: boolean;
};

type SegmentRunConfigFiltersValue = Partial<ReportRunConfig>;

const baseClassName = 'segment-report-run-filters';

export const SegmentRunConfigFilters = forwardRef<SegmentRunConfigFiltersRef, SegmentRunConfigFiltersProps>((
    {
        className,
        onChange,
        onValidityChange,
        name,
        summary,
        includeDemographics,
        includeDemographicDimensions,
        requireDemographics,
        requireDemographicDimensions,
        includeOmniShopperDemographics,
        requireOmniShopperDemographics,
        includeOmniShopperDemographicDimension,
        requireOmniShopperDemographicDimension,
        maxDemographics,
        maxDemographicDimensions,
        maxDemographicConditions,
        maxDemographicValue,
        ...props
    }, ref) => {
    const {value: contextValue, onChange: onContextChange, sku} = useContext(ReportRunConfigWizardContext);
    const [internalValue, setInternalValue] = useState<SegmentRunConfigFiltersValue>({});
    const [state, setState] = useState<ReportRunWizardState>({
        enableDemographics: false
    });
    const wasValid = useRef<boolean>(false);

    useEffect(() => {

        const isValid = checkValidity();

        if (wasValid.current !== isValid) {
            onValidityChange?.({
                name,
                isValid
            });

            wasValid.current = isValid;
        }
    }, [internalValue]);

    useEffect(() => {
        setInternalValue(contextValue ?? {})
    }, [contextValue]);

    function handleChange(e: ByzzerChangeEvent<unknown>) {        
        onContextChange?.(
            e.name as keyof ReportRunConfig,
            e.value,
            e.data
        );
    }

    useImperativeHandle(ref, () => ({
        get value() {
            return internalValue;
        },
        get isValid() {
            return checkValidity()
        }
    }), [internalValue]);

    const isDemographicDataValid = (isRequired: boolean | undefined, demographics: DemographicCondition[] | undefined) => {
        const demographicsLength = demographics?.length || 0;
        if (!isRequired) {            
            const valid = demographics?.every(({ demographic = [], condition = [], value = [] }) =>
                (demographic.length === 0 && value.length === 0) ||
                (demographic.length > 0 && condition.length > 0 && value.length > 0)
            );            
            return valid;
        } else {
            const valid = demographics?.every(({ demographic = [], condition = [], value = [] }) =>
                demographic.length > 0 && condition.length > 0 && value.length > 0
            );
            return valid && demographicsLength > 0;
        }
    };

    function checkValidity(): boolean {
        const demographicsAreValid = isDemographicDataValid(requireDemographics, internalValue.demographics)
        const demographicDimensionsAreValid = !requireDemographicDimensions || (
            requireDemographicDimensions &&
            Number(internalValue.demographicDimensions?.length) > 0
        )
 
        const omniShopperDemographicsAreValid = isDemographicDataValid(requireOmniShopperDemographics, internalValue.demographics); 
        const omniShopperDemographicDimensionsAreValid = !requireOmniShopperDemographicDimension || (
            requireOmniShopperDemographicDimension &&
            Number(internalValue.demographicDimensions?.length) > 0
        )
        
        return Boolean(
            demographicsAreValid &&
            demographicDimensionsAreValid &&
            omniShopperDemographicsAreValid &&
            omniShopperDemographicDimensionsAreValid
        );
    }    

    function handleEnableDemographicsChange(e: ByzzerChangeEvent<boolean>) {

        if (!e.value) { 
            onContextChange?.('demographics', []); // Reset demographicfilter selections
        }

        setState(state => ({
            ...state,
            enableDemographics: e.value
        }))
    }


    return (
        <FilterGroup className={classnames(baseClassName, className)} {...props}>
            {summary && <div className={`${baseClassName}__summary`}>{summary}</div>}
            <FilterSection onlyRenderIf={includeDemographicDimensions}>
                <DemographicDimensionSelect
                    className={className}
                    name={'demographicDimensions'}
                    onChange={handleChange}
                    value={internalValue.demographicDimensions}
                    placeholder={'Select from the list'}
                    maxSelections={maxDemographicDimensions ?? 5} // todo check with product team to ensure config in alby is set for this.  temporarily keeping 5 until alby is final
                    onlyRenderIf={includeDemographicDimensions}
                    required={requireDemographicDimensions}
                    sku={sku}
                />
            </FilterSection>
            <FilterSection onlyRenderIf={includeDemographics}>
                <SelectorLabelInfo sku={sku!} selectorCode={'demographicDimensionsMessage'} max={maxDemographics} />

                <ByzzerRadioGroup
                    value={state.enableDemographics}
                    onChange={handleEnableDemographicsChange}
                    name={'enableDemographics'}
                >
                    <ByzzerRadio
                        value={false}
                        label={
                            <SelectorLabelInfo
                                sku={sku!}
                                selectorCode={'enableDemographicsNo'}
                                isLabelBold={true}
                                max={maxDemographics}
                            />
                        }
                    />
                    <ByzzerRadio
                        value={true}
                        label={
                            <SelectorLabelInfo
                                sku={sku!}
                                selectorCode={'enableDemographicsYes'}
                                isLabelBold={true}
                                max={maxDemographics}
                            />
                        }
                    />
                </ByzzerRadioGroup>
                <DemographicCriteriaBuilder
                    // className={className}
                    name={'demographics'}
                    onChange={handleChange}
                    value={internalValue.demographics}
                    placeholder={'Select from the list'}
                    searchableFields={['ZIP_CODE']}
                    maxConditions={maxDemographics ?? 5} // todo check with product team to ensure config in alby is set for this.  temporarily keeping 5 until alby is final
                    onlyRenderIf={state.enableDemographics}
                />
            </FilterSection>
            <FilterSection onlyRenderIf={includeOmniShopperDemographics}>
                <SelectorLabelInfo
                    sku={sku!}
                    selectorCode={'oshDemographics'}
                    max={maxDemographics}
                    isLabelBold={true}
                    required={requireOmniShopperDemographics}
                />
                <ByzzerOshDemographicCriteriaBuilder
                    className={className}
                    name={'demographics'}
                    onChange={handleChange}
                    value={internalValue.demographics}
                    placeholder={'Select from the list'}
                    maxConditions={maxDemographicConditions ?? 1}
                    maxAllowedValues={maxDemographicValue}
                />
            </FilterSection>
            <FilterSection onlyRenderIf={includeOmniShopperDemographicDimension}>
                <ByzzerOshDemographicDimensionSelect
                    className={className}
                    name={'demographicDimensions'}
                    onChange={handleChange}
                    value={internalValue.demographicDimensions}
                    placeholder={'Select from the list'}
                    sku={sku}
                    required={requireOmniShopperDemographicDimension}
                    maxSelections={maxDemographicDimensions ?? 5}
                />
            </FilterSection>
        </FilterGroup>
    );

})

SegmentRunConfigFilters.displayName = 'SegmentRunConfigFilters'; // original change to match release
